import { useIsWorkflowsEnabled } from "@circle-react-shared/Workflows/hooks/useIsWorkflowsEnabled";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import { useIsEmailHubNewsletterPhaseAEnabled } from "@circle-react/hooks/useIsEmailHubNewsletterPhaseAEnabled";
import { useIsEmailHubNewsletterPhaseEEnabled } from "@circle-react/hooks/useIsEmailHubNewsletterPhaseEEnabled";
import { adminDashboardRoutes } from "@circle-react/lib/routes/adminDashboard";
import { coursesRoutes } from "@circle-react/lib/routes/courses";
import {
  newsletterHomePath,
  newsletterRoutes,
} from "@circle-react/lib/routes/newsletter";
import { settingsRoutes } from "@circle-react/lib/routes/settings";
import { siteRoutes } from "@circle-react/lib/routes/site";
import { workflowsRoutes } from "@circle-react/lib/routes/workflows";
import { useIsCoursesV2Enabled } from "react/hooks/courses/useIsCoursesV2Enabled";

import { t } from "@/i18n-js/instance";
import { useNewsletterActivation } from "@/react/components/SettingsApp/Newsletters/hooks/useNewsletterActivation";
import {
  isCommunityAdmin,
  isCommunityModerator,
} from "@/react/helpers/communityMemberHelpers";
import { useDevelopersAccessFlags } from "@/react/hooks/developers/useDevelopersAccessFlags";
import { useBrandedAppsFeatureFlags } from "@/react/hooks/useBrandedAppsFeatureFlags";

import type { SidebarLink } from "./types";

export const BACK_TO_COMMUNITY_PATH_KEY = "back-to-community";

export const useCollapsedSidebarLinksV3 = (): SidebarLink[] => {
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const { isCircleV4 } = useCircleV4();
  const isWorkflowsEnabled = useIsWorkflowsEnabled();
  const isHomePageEnabled = Boolean(currentCommunity?.home_page_enabled);
  const { isOverviewTabEnabled } = useDevelopersAccessFlags();
  const { brandedAppsBuilder, brandedApp } = useBrandedAppsFeatureFlags();

  const isAffiliatesEnabled = Boolean(
    currentCommunity?.affiliates_feature_flag_enabled &&
    isCommunityAdmin(currentCommunityMember),
  );

  const isCoursesV2Enabled = useIsCoursesV2Enabled();
  const isNewsletterEnabled = useIsEmailHubNewsletterPhaseAEnabled();
  const isNewsletterOverviewEnabled = useIsEmailHubNewsletterPhaseEEnabled();
  const { isActivationRequired: isNewsletterActivationRequired } =
    useNewsletterActivation();
  const isCommunityBotEnabled = isCommunityAdmin(currentCommunityMember);
  const hasAIAgentsAccess = Boolean(
    currentCommunity?.ai_agents_feature_flag_enabled,
  );

  const links: SidebarLink[] = [
    {
      pathKey: BACK_TO_COMMUNITY_PATH_KEY,
      label: t("settings_nav_v3.back_to_community.label"),
      icon: "20-back-to-community",
      path: isHomePageEnabled ? "/feed" : "/",
      enabled:
        isCommunityAdmin(currentCommunityMember) ||
        isCommunityModerator(currentCommunityMember),
    },
    {
      pathKey: "admin_dashboard",
      label: t("sidebar_admin_dashboard"),
      icon: "sw-home",
      path: adminDashboardRoutes.index(),
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "members",
      label: t("settings_nav_v3.members.label"),
      icon: "20-members",
      path: settingsNavPathV3.members.manage,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "content",
      label: t("settings_nav_v3.content.label"),
      icon: "20-content",
      path: isCommunityAdmin(currentCommunityMember)
        ? settingsNavPathV3.content.files
        : settingsNavPathV3.content.posts,
      enabled:
        isCommunityAdmin(currentCommunityMember) ||
        isCommunityModerator(currentCommunityMember),
    },
    {
      pathKey: "events",
      label: t("settings_nav_v3.events.label"),
      icon: "20-event-ticket",
      path: "/settings/events",
      enabled: isCommunityAdmin(currentCommunityMember) && isCircleV4,
    },
    {
      pathKey: "email",
      label: t("settings_nav_v3.emails.title"),
      icon: "20-send",
      path: settingsNavPathV3.email.overview,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "newsletter",
      label: t("settings_nav_v3.newsletter.title"),
      icon: "20-email-broadcast",
      path: isNewsletterActivationRequired
        ? newsletterRoutes.activate()
        : newsletterHomePath(isNewsletterOverviewEnabled),
      enabled: isCommunityAdmin(currentCommunityMember) && isNewsletterEnabled,
    },
    {
      pathKey: "courses",
      label: t("settings_nav_v3.courses.title"),
      icon: "20-graduate-cap-hollow",
      path: coursesRoutes.overview(),
      enabled: isCommunityAdmin(currentCommunityMember) && isCoursesV2Enabled,
    },
    {
      pathKey: "workflows",
      label: t("settings_nav_v3.workflows.label"),
      icon: "20-workflows",
      path: workflowsRoutes.list(),
      enabled: isCommunityAdmin(currentCommunityMember) && isWorkflowsEnabled,
    },
    {
      pathKey: "analytics_v2",
      label: t("settings_nav_v3.analytics.label"),
      icon: "20-analytics",
      path: settingsNavPathV3.analytics_v2.index,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "community_bot",
      label: t("settings_nav_v3.community_bot.label"),
      icon: "20-community-bot",
      path: settingsNavPathV3.community_bot.knowledge,
      enabled: isCommunityBotEnabled,
    },
    {
      pathKey: "community_inbox",
      label: t("settings_nav_v3.community_inbox.label"),
      icon: "20-community-inbox",
      path: settingsNavPathV3.community_inbox.index,
      enabled: isCommunityBotEnabled && hasAIAgentsAccess,
    },
    {
      pathKey: "paywalls",
      label: t("settings_nav_v3.payments.label"),
      icon: "20-payments",
      path: settingsNavPathV3.paywalls.index,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "affiliates",
      label: t("settings_nav_v3.paywalls_affiliates.label"),
      icon: "20-paywalls-affiliates",
      path: settingsNavPathV3.affiliates.settings,
      enabled: isAffiliatesEnabled,
    },
    {
      pathKey: "plans",
      label: t("settings_nav_v3.plans.label"),
      icon: "20-plans",
      path: settingsNavPathV3.plans.general,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
  ];

  if (brandedApp.isEnabled) {
    links.push({
      pathKey: "plus",
      label: t("settings_nav_v3.settings.branded_app"),
      icon: "20-mobile-settings",
      path: brandedAppsBuilder.isEnabled
        ? settingsNavPathV3.plus.index
        : settingsNavPathV3.plus.lite,
      enabled: isCommunityAdmin(currentCommunityMember),
    });
  }

  return links.concat([
    {
      pathKey: "site",
      label: t("settings_nav_v3.site.label"),
      icon: "20-site",
      path: siteRoutes.index(),
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "developers",
      label: t("settings_nav_v3.developers.title"),
      icon: "20-code-tag",
      path: isOverviewTabEnabled
        ? settingsNavPathV3.developers.overview
        : settingsNavPathV3.developers.api,
      enabled: isCommunityAdmin(currentCommunityMember),
    },
    {
      pathKey: "settings",
      label: t("settings_nav_v3.settings.label"),
      icon: "20-nav-settings",
      path: settingsRoutes.general(),
      enabled: isCommunityAdmin(currentCommunityMember),
    },
  ]);
};
